import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, Badge, Spinner, Button, Input } from '../../components/ui';
import { 
  Building, Search, Filter, RefreshCw, 
  CheckCircle, XCircle, Clock, AlertCircle,
  Plus, DollarSign, Eye, Trash2, Shield,
  Users, ChevronRight
} from 'lucide-react';
import './Admin.css';

const AdminOrganizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(false);
  const { request } = useApi();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/admin/organizations').catch(() => []);
      setOrganizations(data || []);
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orgId, newStatus) => {
    if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`)) return;
    
    setUpdating(true);
    try {
      await request('put', `/api/admin/organizations/${orgId}/status`, { status: newStatus });
      fetchOrganizations();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success"><CheckCircle size={12} /> Active</Badge>;
      case 'PENDING':
        return <Badge variant="warning"><Clock size={12} /> Pending</Badge>;
      case 'SUSPENDED':
        return <Badge variant="danger"><XCircle size={12} /> Suspended</Badge>;
      default:
        return <Badge variant="default">{status || 'Unknown'}</Badge>;
    }
  };

  const getWaStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="success">Onboarded</Badge>;
      case 'PENDING':
        return <Badge variant="warning">Pending</Badge>;
      default:
        return <Badge variant="default">Not Started</Badge>;
    }
  };

  const filteredOrgs = organizations.filter(org => {
    const matchesSearch = org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || org.status === statusFilter.toUpperCase();
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Organizations</h1>
          <p className="admin-subtitle">Manage all organizations on the platform</p>
        </div>
        <button className="refresh-btn" onClick={fetchOrganizations}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Summary */}
      <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '24px' }}>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #8B5CF6' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{organizations.length}</div>
            <div className="admin-stat-label">Total Organizations</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #10B981' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{organizations.filter(o => o.status === 'ACTIVE').length}</div>
            <div className="admin-stat-label">Active</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #F59E0B' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{organizations.filter(o => o.status === 'PENDING').length}</div>
            <div className="admin-stat-label">Pending</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #EF4444' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{organizations.filter(o => o.status === 'SUSPENDED').length}</div>
            <div className="admin-stat-label">Suspended</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="admin-filters">
        <div className="search-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <Filter size={18} className="filter-icon" />
          <select 
            className="filter-select" 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Organizations Table */}
      <Card className="admin-table-container" padding="none">
        <div className="table-header">
          <h3 className="table-title">All Organizations</h3>
          <div className="table-info">{filteredOrgs.length} organizations</div>
        </div>
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Contact</th>
                <th>WhatsApp</th>
                <th>Credits</th>
                <th>Status</th>
                <th>WA Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan={7}>
                    <div className="empty-state">
                      <Building size={48} className="empty-icon" />
                      <h3>No organizations found</h3>
                      <p>Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredOrgs.map((org) => (
                  <tr key={org.id}>
                    <td>
                      <div className="org-name">{org.name}</div>
                      <div className="org-id">ID: {org.id?.slice(0, 8)}</div>
                    </td>
                    <td>
                      <div className="contact-email">{org.email}</div>
                      <div className="contact-phone">{org.phoneNumber || '-'}</div>
                    </td>
                    <td className="wa-number">{org.whatsappBusinessId || '-'}</td>
                    <td>
                      <span className="credits-amount">
                        <DollarSign size={14} />
                        {org.creditBalance?.toLocaleString() || 0}
                      </span>
                    </td>
                    <td>{getStatusBadge(org.status)}</td>
                    <td>{getWaStatusBadge(org.waOnboardingStatus)}</td>
                    <td>
                      <div className="actions-cell">
                        {org.status === 'PENDING' && (
                          <button 
                            className="action-btn approve"
                            onClick={() => updateStatus(org.id, 'ACTIVE')}
                            disabled={updating}
                          >
                            <CheckCircle size={14} /> Approve
                          </button>
                        )}
                        {org.status === 'ACTIVE' && (
                          <button 
                            className="action-btn suspend"
                            onClick={() => updateStatus(org.id, 'SUSPENDED')}
                            disabled={updating}
                          >
                            <XCircle size={14} /> Suspend
                          </button>
                        )}
                        {org.status === 'SUSPENDED' && (
                          <button 
                            className="action-btn activate"
                            onClick={() => updateStatus(org.id, 'ACTIVE')}
                            disabled={updating}
                          >
                            <CheckCircle size={14} /> Reactivate
                          </button>
                        )}
                        <button 
                          className="action-btn view"
                          onClick={() => window.location.href = `/admin/organizations/${org.id}`}
                        >
                          <Eye size={14} /> View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AdminOrganizations;